<template>
  <div>
    <div class="text-red-darken-2 font-weight-bold">
      <span v-if="spelling && submitDisabledMessage">{{
        submitDisabledMessage
      }}</span>
      <ul v-if="spellingErrors.length > 0">
        <li v-for="(error, idx) in spellingErrors" :key="idx">
          {{ error }}
        </li>
      </ul>
    </div>

    This spelling appears in the following texts:
    <v-data-table-server
      v-model="selectedDiscourses"
      :headers="discourseResultHeaders"
      :items="discourseSearchResults"
      :loading="searchDiscourseLoading"
      :items-length="totalDiscourseResults"
      v-model:page="discourseOptions.page"
      v-model:items-per-page="discourseOptions.itemsPerPage"
      :items-per-page-options="[10, 25, 50, 100]"
      item-value="uuid"
      return-object
      show-select
    >
      <template #[`item.textName`]="{ item }">
        <router-link
          :to="`/epigraphies/${item.textUuid}/${item.uuid}`"
          target="_blank"
          >{{ item.textName }}</router-link
        >
      </template>
      <template #[`item.line`]="{ item }">
        <span>{{ formatLineNumber(item.line, false) }}</span>
      </template>
      <template #[`item.readings`]="{ item }">
        <span v-html="renderedReading(item)" />
      </template>
    </v-data-table-server>
    <v-row>
      <v-spacer />
      <v-tooltip :disabled="!submitDisabledMessage" location="bottom">
        <template #activator="{ props }">
          <span v-bind="props">
            <v-btn
              color="primary"
              :disabled="hasError || isTyping"
              :loading="submitLoading"
              @click="submit"
              class="test-submit-btn"
            >
              Submit
            </v-btn>
          </span>
        </template>
        <span>{{ submitDisabledMessage }}</span>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script lang="ts">
import { clientErrorMessage } from '@/utils/errorMessages';
import {
  SearchDiscourseSpellingRow,
  DictionaryForm,
  FormSpelling,
} from '@oare/types';
import {
  defineComponent,
  ref,
  Ref,
  watch,
  PropType,
  computed,
  inject,
} from 'vue';
import sl from '@/serviceLocator';
import { DataTableHeader } from 'vuetify';
import utils from '@/utils';
import { ReloadKey } from '../../../../../index.vue';
import { spellingHtmlReading } from '@oare/oare';
import { formatLineNumber } from '@oare/oare/src/tabletUtils';

export default defineComponent({
  name: 'SpellingDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<DictionaryForm>,
      required: true,
    },
    spelling: {
      type: Object as PropType<FormSpelling | null>,
      default: null,
    },
    spellingInput: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const _ = sl.get('lodash');
    const totalDiscourseResults = ref(0);
    const discourseOptions = ref({
      page: 1,
      itemsPerPage: 50,
    });
    const submitLoading = ref(false);
    const selectedDiscourses: Ref<SearchDiscourseSpellingRow[]> = ref([]);

    const discourseSearchResults: Ref<SearchDiscourseSpellingRow[]> = ref([]);
    const searchDiscourseLoading = ref(false);
    const spellingErrors = ref<string[]>([]);
    const isTyping = ref(false);

    const discourseResultHeaders: Ref<DataTableHeader[]> = ref([
      {
        title: 'Text',
        key: 'textName',
        width: 100,
      },
      {
        title: 'Line',
        key: 'line',
        width: 75,
      },
      {
        title: 'Reading',
        key: 'readings',
      },
    ]);

    const reload = inject(ReloadKey);

    const spellingExists = computed(
      () =>
        !props.spelling &&
        props.form.spellings.map(f => f.spelling).includes(props.spellingInput)
    );

    const submitDisabledMessage = computed(() => {
      if (spellingExists.value) {
        return 'The spelling you have typed already exists in the form';
      } else if (!props.spellingInput) {
        return 'You cannot submit an empty spelling';
      } else if (spellingErrors.value.length > 0) {
        return 'The spelling you entered contains errors';
      } else if (
        props.spelling &&
        props.spellingInput === props.spelling.spelling &&
        selectedDiscourses.value.length === 0
      ) {
        return 'You must link the text to unlinked discourses and/or update the spelling.';
      }
      return '';
    });

    const hasError = computed(
      () => !!submitDisabledMessage.value || spellingErrors.value.length > 0
    );

    const addSpelling = async () => {
      try {
        submitLoading.value = true;
        await server.addSpelling({
          formUuid: props.form.uuid,
          spelling: props.spellingInput,
          discourseUuids: selectedDiscourses.value.map(row => row.uuid),
        });
        actions.showSnackbar('Successfully added spelling');
        reload && reload();
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to add spelling to form',
          err as Error
        );
      } finally {
        submitLoading.value = false;
      }
    };

    const editSpelling = async () => {
      if (!props.spelling) {
        actions.showErrorSnackbar(
          'Failed to update form spelling because spelling is null'
        );
        return;
      }

      try {
        submitLoading.value = true;
        await server.updateSpelling(
          props.spelling.uuid,
          props.spellingInput,
          selectedDiscourses.value.map(row => row.uuid)
        );
        actions.showSnackbar('Successfully updated spelling');
        reload && reload();
      } catch (err) {
        actions.showErrorSnackbar(
          clientErrorMessage(err, 'Failed to update form spelling'),
          err as Error
        );
      } finally {
        submitLoading.value = false;
      }
    };

    const submit = () => {
      if (props.spelling) {
        editSpelling();
      } else {
        addSpelling();
      }
    };

    const renderedReading = (row: SearchDiscourseSpellingRow) => {
      return row.readings
        .map(reading => {
          if (reading.wordOnTablet === row.wordOnTablet) {
            return `<mark>${spellingHtmlReading(reading.spelling)}</mark>`;
          } else if (reading.transcription) {
            return reading.transcription;
          } else {
            return spellingHtmlReading(reading.spelling);
          }
        })
        .join(' ');
    };

    const checkSpelling = async (spelling: string) => {
      try {
        const { errors } = await server.checkSpelling(spelling);
        spellingErrors.value = errors;
      } catch (err) {
        actions.showErrorSnackbar('Failed to check spelling', err as Error);
      }
    };

    const searchDiscourse = async (newSpelling: string) => {
      try {
        searchDiscourseLoading.value = true;
        discourseSearchResults.value = [];
        const { totalResults, rows } = await server.searchSpellingDiscourse(
          newSpelling,
          {
            page: discourseOptions.value.page - 1,
            limit: discourseOptions.value.itemsPerPage,
          }
        );

        discourseSearchResults.value = rows;
        totalDiscourseResults.value = totalResults;
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to search discourse spellings',
          err as Error
        );
      } finally {
        searchDiscourseLoading.value = false;
      }
    };

    watch(
      () => props.spellingInput,
      () => (isTyping.value = true)
    );

    watch(
      () => props.spellingInput,
      _.debounce(async (newSpelling: string) => {
        if (newSpelling) {
          await searchDiscourse(newSpelling);
          await checkSpelling(newSpelling);
          isTyping.value = false;
        } else {
          discourseSearchResults.value = [];
          spellingErrors.value = [];
        }
      }, 500)
    );

    watch(
      discourseOptions,
      () => {
        if (props.spellingInput) {
          searchDiscourse(props.spellingInput);
        }
      },
      { deep: true }
    );

    watch(
      () => props.modelValue,
      open => {
        if (!open) {
          discourseSearchResults.value = [];
          selectedDiscourses.value = [];
          discourseOptions.value.page = 1;
        } else {
          if (props.spelling) {
            searchDiscourse(props.spellingInput);
            checkSpelling(props.spellingInput);
          }
        }
      },
      { immediate: true }
    );

    return {
      isTyping,
      discourseSearchResults,
      discourseResultHeaders,
      searchDiscourseLoading,
      renderedReading,
      totalDiscourseResults,
      discourseOptions,
      spellingExists,
      submitDisabledMessage,
      submitLoading,
      addSpelling,
      selectedDiscourses,
      formGrammarString: utils.formGrammarString,
      submit,
      spellingErrors,
      hasError,
      spellingHtmlReading,
      formatLineNumber,
    };
  },
});
</script>
